import { useRef, useState } from 'react'
import classNames from 'classnames'
import isEqual from 'lodash-es/isEqual'

import { ScreenContainerRef } from '../../../context'
import { useMinimizedModalsDirtyGuard } from '../../../hooks'
import { AccordionAnimatedContent } from '../../Accordion'
import { isDraftOpenAction, isDraftSaveAction } from '../../Draft'
import { MINIMIZED_MODAL_SCOPE } from '../../Modal'
import {
  useEmailCreationFormInitialValues,
  useEmailDraft,
  useEmailGetActiveAccountForCreate,
  useEmailProfileActionsMessage,
  useEmailReply,
} from '../Email.hooks'
import { isDirtyEmailCreationForm } from '../EmailCreationForm'
import { EmailDraftCloseActionSelectModal } from '../EmailDraftCloseActionSelectModal'

import { useEmailProfileMessageFullScreenModalReplyForm } from './EmailProfileMessage.hooks'
import { getEmailProfileMessageAiLocalContext } from './EmailProfileMessage.utils'
import { EmailProfileMessageBody } from './EmailProfileMessageBody'
import { EmailProfileMessageHeader } from './EmailProfileMessageHeader'
import { EmailProfileMessageReplyForm } from './EmailProfileMessageReplyForm'

import * as styles from './EmailProfileMessage.module.css'

function EmailProfileMessage({
  PriorityFieldComponent,
  actionsConfig,
  aiLocalContextObjectId,
  aiLocalContextType,
  attachProps,
  bodyProps,
  className = styles.EmailProfileMessage,
  data,
  headerProps,
  isExpanded,
  minimizedModalScope = MINIMIZED_MODAL_SCOPE.EMAIL_PROFILE,
  recipientsFilterModalInitialValues,
  ref,
  replyConfig,
  singleTemplateFolder,
  templateEntityApiParams,
  templateListApiParams,
  withServiceAccount = false,
}) {
  const [modal, setModal] = useState(null)

  const replyFormRef = useRef(null)

  const { activeAccountForCreate } = useEmailGetActiveAccountForCreate({
    withServiceAccount,
  })

  const { setDraft: setEmailDraft } = useEmailDraft()

  const emailInitial = useEmailCreationFormInitialValues({
    account: activeAccountForCreate,
  })

  function handleCloseModal() {
    setModal(null)
  }

  const {
    handleCancelReply,
    handleClickForward,
    handleClickRemoveReplyDraft,
    handleClickReply,
    handleClickReplyAll,
    handleInitReplyEditor,
    handleSubmitReply,
    handleSuccessReply,
    replyCreationAction,
    replyFormEnabled,
    replyFormInitialValues,
    setReplyFormInitialValues,
    toggleExpandConversation,
  } = useEmailReply({
    ...replyConfig,
    data,
    emailInitial,
    onCloseModal: handleCloseModal,
    onSetModal: setModal,
  })

  const {
    handleClickAction,
    handleClickRemoveCopiedEntity,
    handleViewEntireMessage,
    requestCreationModal,
  } = useEmailProfileActionsMessage({
    ...actionsConfig,
    item: data,
    minimizedModalScope,
    onForward: handleClickForward,
    onReply: handleClickReply,
    onReplyAll: handleClickReplyAll,
  })

  const profileClassName = classNames(className, {
    [styles.isExpanded]: isExpanded,
  })

  function getAiLocalContext() {
    if (aiLocalContextType) {
      // email_reply uses the message UUID; service_request_email uses the request id
      const objectId = aiLocalContextObjectId ?? data?.uuid

      return objectId
        ? getEmailProfileMessageAiLocalContext({
            messageUuid: data?.uuid,
            objectId,
            type: aiLocalContextType,
          })
        : undefined
    }
  }

  const aiLocalContext = getAiLocalContext()

  function getContainerIsDirty() {
    return isDraftOpenAction(replyCreationAction)
  }

  function getIsDirtyReplyModalForm({ initialValues, values }) {
    return (
      isDirtyEmailCreationForm({ initialValues, values }) ||
      !isEqual(replyFormInitialValues, replyFormRef.current.values)
    )
  }

  function getIsOpenReplyFormDirty() {
    return (
      replyFormRef.current != null &&
      getIsDirtyReplyModalForm({
        initialValues: replyFormRef.current.initialValues,
        values: replyFormRef.current.values,
      })
    )
  }

  function handleLeaveConfirmSubmit(action) {
    setEmailDraft(
      isDraftSaveAction(action) ? replyFormRef.current.values : null,
      0
    )
  }

  useMinimizedModalsDirtyGuard({
    getIsDirty: getIsOpenReplyFormDirty,
    isEnabled: replyFormEnabled,
    leaveConfirmation: {
      Component: EmailDraftCloseActionSelectModal,
      onSubmit: handleLeaveConfirmSubmit,
    },
    scope: minimizedModalScope,
  })

  const { handleMakeReplyFormFullScreen } =
    useEmailProfileMessageFullScreenModalReplyForm({
      PriorityFieldComponent,
      aiLocalContext,
      attachProps,
      getContainerIsDirty,
      getIsDirty: getIsDirtyReplyModalForm,
      handleCancelReply,
      handleClickRemoveReplyDraft,
      handleInitReplyEditor,
      handleSubmitReply,
      handleSuccessReply,
      recipientsFilterModalInitialValues,
      setModal,
      setReplyFormInitialValues,
      singleTemplateFolder,
      templateEntityApiParams,
      templateListApiParams,
      withServiceAccount,
    })

  return (
    <div className={profileClassName} ref={ref}>
      <EmailProfileMessageHeader
        {...headerProps}
        actionsProps={{
          ...headerProps?.actionsProps,
          onClickAction: handleClickAction,
        }}
        data={data}
      />
      <AccordionAnimatedContent isOpen={isExpanded}>
        <EmailProfileMessageBody
          {...bodyProps}
          data={data}
          footerActionsProps={{
            disabled: replyFormEnabled,
            onClickForward: handleClickForward,
            onClickReply: handleClickReply,
            onClickReplyAll: handleClickReplyAll,
          }}
          onClickRemoveCopiedEntity={handleClickRemoveCopiedEntity}
          onViewEntireMessage={handleViewEntireMessage}
        />

        {replyFormEnabled ? (
          <EmailProfileMessageReplyForm
            PriorityFieldComponent={PriorityFieldComponent}
            aiLocalContext={aiLocalContext}
            attachProps={attachProps}
            externalRef={replyFormRef}
            formBodyActionsProps={{
              onMakeFormFullScreen: handleMakeReplyFormFullScreen,
              onRemoveDraft: handleClickRemoveReplyDraft,
            }}
            getContainerIsDirty={getContainerIsDirty}
            initialValues={replyFormInitialValues}
            recipientsFilterModalInitialValues={
              recipientsFilterModalInitialValues
            }
            singleTemplateFolder={singleTemplateFolder}
            templateEntityApiParams={templateEntityApiParams}
            templateListApiParams={templateListApiParams}
            withServiceAccount={withServiceAccount}
            onCancel={handleCancelReply}
            onInitEditor={handleInitReplyEditor}
            onSubmit={handleSubmitReply}
            onSuccess={handleSuccessReply}
            onToggleExpandConversation={toggleExpandConversation}
          />
        ) : null}
        <ScreenContainerRef value={null}>
          {modal}
          {requestCreationModal}
        </ScreenContainerRef>
      </AccordionAnimatedContent>
    </div>
  )
}

export default EmailProfileMessage
